import { ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Link, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { HmsApprovalMode, TemplateFactorInput, TemplateLanguage, TemplateOverview } from '../../../api/hms';
import { Routes } from '../../../enums/Routes';
import {
  useApproveHmsTemplateVersionMutation,
  useDiscardHmsTemplateDraftMutation,
  useHmsTemplateQuery,
  useSaveHmsTemplateDraftMutation,
  useSaveHmsTemplateLanguagesMutation,
  useSaveHmsTranslationsMutation,
  useSuggestHmsTranslationsMutation,
  useSetHmsTemplateAvailabilityMutation,
  useSubmitHmsTemplateDraftMutation,
  useWithdrawHmsTemplateVersionMutation,
} from '../../../hooks/queries/hms/useHmsQueries';
import { FactorList } from './components/FactorList';
import { VersionView } from './components/VersionView';
import { TranslationView } from './components/TranslationView';
import { missingLanguagesByQuestion } from './languagesView';
import { APPROVAL_COMMENT_ID, TemplateSidePanel } from './components/TemplateSidePanel';
import { ReturnedAlert } from '../components/ReturnedAlert';
import { approvalCommentMissing, approvalCommentRequired, initialApprovalMode } from '../hmsApproval';
import layout from '../hmsPage.module.css';

export const HRRiskFactors = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useHmsTemplateQuery();

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('hrRiskFactors.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      );
    }
    if (error || !data) {
      return (
        <div className={layout.loading}>
          <Alert data-color="danger">
            <Heading level={2} data-size="2xs">
              {t('hrRiskFactors.error.title')}
            </Heading>
            <Paragraph>{t('hrRiskFactors.error.message')}</Paragraph>
          </Alert>
        </div>
      );
    }
    return <RiskFactorsEditor overview={data} />;
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <Link asChild data-size="sm" className={layout.startAligned}>
            <RouterLink to={Routes.HRRiskMatrixFullPath}>{t('hrRiskFactors.back')}</RouterLink>
          </Link>
          <span className={layout.eyebrow}>
            {data?.organizationName
              ? t('hms.eyebrowWithOrganization', { organization: data.organizationName })
              : t('hms.eyebrow')}
          </span>
          <Heading level={1} data-size="xl">
            {t('hrRiskFactors.title')}
          </Heading>
          <Paragraph className={layout.intro}>{t('hrRiskFactors.intro')}</Paragraph>
        </div>
      </section>
      {content()}
    </div>
  );
};

const RiskFactorsEditor = ({ overview }: { overview: TemplateOverview }): ReactElement => {
  const { t } = useTranslation();
  const [viewId, setViewId] = useState<number | null>(null);
  const [translating, setTranslating] = useState<string | null>(null);
  const { current, draft, hasMainSafetyRep, mainSafetyRepIsYou } = overview;
  const commentRequired = approvalCommentRequired(overview);
  const [mode, setMode] = useState<HmsApprovalMode>(initialApprovalMode(hasMainSafetyRep));
  const [comment, setComment] = useState('');
  const [triedComment, setTriedComment] = useState(false);
  const [justApproved, setJustApproved] = useState(false);

  const save = useSaveHmsTemplateDraftMutation();
  const discard = useDiscardHmsTemplateDraftMutation();
  const submit = useSubmitHmsTemplateDraftMutation();
  const approve = useApproveHmsTemplateVersionMutation();
  const withdraw = useWithdrawHmsTemplateVersionMutation();
  const availability = useSetHmsTemplateAvailabilityMutation();
  const saveLanguages = useSaveHmsTemplateLanguagesMutation();
  const saveTranslations = useSaveHmsTranslationsMutation();
  const suggest = useSuggestHmsTranslationsMutation();

  const pending = draft?.status === 'PENDING';
  const dirty = draft?.status === 'DRAFT';
  const factors = draft?.factors ?? current.factors;
  const languages = (draft ?? current).languages;
  const translatingLanguage = languages.find((language) => language.code === translating) ?? null;
  const nextVersion = draft?.version ?? current.version + 1;

  const onSave = (next: TemplateFactorInput[], onSaved?: () => void) =>
    save.mutate(next, {
      onSuccess: () => {
        setJustApproved(false);
        onSaved?.();
      },
    });

  const approved = () => {
    setJustApproved(true);
    setComment('');
    setTriedComment(false);
  };

  const send = () => {
    if (approvalCommentMissing(mode, comment, commentRequired)) {
      setTriedComment(true);
      document.getElementById(APPROVAL_COMMENT_ID)?.focus();
      return;
    }
    submit.mutate(
      { mode, comment: comment.trim() || undefined },
      { onSuccess: (next) => (next.draft === null ? approved() : setTriedComment(false)) },
    );
  };

  const banner = (() => {
    if (justApproved) return { color: 'success', text: t('hrRiskFactors.banner.approved', { version: current.version }) };
    if (pending) return { color: 'warning', text: t('hrRiskFactors.banner.pending') };
    if (dirty) return { color: 'warning', text: t('hrRiskFactors.banner.draft', { version: current.version }) };
    return { color: 'info', text: t('hrRiskFactors.banner.inUse', { version: current.version, next: nextVersion }) };
  })();

  return (
    <div className={layout.layout}>
      <div className={layout.main}>
        {draft?.status === 'DRAFT' && draft.returned ? (
          <ReturnedAlert version={draft.version} returned={draft.returned} />
        ) : (
          <Alert data-color={banner.color as 'success' | 'warning' | 'info'} data-size="sm">
            <Paragraph data-size="sm">{banner.text}</Paragraph>
          </Alert>
        )}
        {save.isError && (
          <Alert data-color="danger">
            <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
          </Alert>
        )}
        {translatingLanguage && !pending ? (
          <TranslationView
            language={translatingLanguage}
            onBack={() => setTranslating(null)}
            onSave={(entries) =>
              saveTranslations.mutate({ language: translatingLanguage.code, entries }, { onSuccess: () => setJustApproved(false) })
            }
            saveFailed={saveTranslations.isError}
            onSuggest={() => suggest.mutate(translatingLanguage.code, { onSuccess: () => setJustApproved(false) })}
            suggesting={suggest.isPending}
            suggestFailed={suggest.isError}
          />
        ) : viewId !== null ? (
          <VersionView
            id={viewId}
            versions={overview.versions}
            backLabel={t(dirty || pending ? 'hrRiskFactors.version.backToDraft' : 'hrRiskFactors.version.backToFactors')}
            onBack={() => setViewId(null)}
            onAvailabilityChange={(id, availableForNew) => availability.mutate({ id, availableForNew })}
            savingAvailability={availability.isPending}
            availabilityFailed={availability.isError}
          />
        ) : (
          <FactorList
            factors={factors}
            current={current.factors}
            missingLanguages={missingLanguagesByQuestion(languages)}
            title={
              dirty || pending
                ? t('hrRiskFactors.list.titleDraft', { version: nextVersion })
                : t('hrRiskFactors.list.title', { version: current.version })
            }
            locked={pending}
            saving={save.isPending}
            onSave={onSave}
          />
        )}
      </div>
      <TemplateSidePanel
        overview={overview}
        viewId={viewId}
        onView={(id) => {
          setViewId(id);
          setTranslating(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        approval={{
          hasMainSafetyRep,
          mainSafetyRepIsYou,
          mode,
          onModeChange: setMode,
          comment,
          onCommentChange: setComment,
          commentError:
            triedComment && approvalCommentMissing(mode, comment, commentRequired)
              ? t('hms.approval.commentError')
              : undefined,
        }}
        send={{ run: send, pending: submit.isPending }}
        discard={{ run: () => discard.mutate(undefined), pending: discard.isPending }}
        approve={{
          run: (id, approvalComment) => approve.mutate({ id, comment: approvalComment }, { onSuccess: approved }),
          pending: approve.isPending,
        }}
        withdraw={{ run: (id) => withdraw.mutate(id), pending: withdraw.isPending }}
        actionFailed={submit.isError || discard.isError || approve.isError || withdraw.isError}
        saveLanguages={{
          run: (next: TemplateLanguage[], onSaved?: () => void) =>
            saveLanguages.mutate(next, {
              onSuccess: () => {
                setJustApproved(false);
                onSaved?.();
              },
            }),
          pending: saveLanguages.isPending,
          failed: saveLanguages.isError,
        }}
        onTranslate={(code) => {
          setTranslating(code);
          setViewId(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
};
